import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Save, Trash2, Users2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { ROLES, JADWAL_KERJA } from "@/constants/roles";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";

export default function AdminSettings() {
  const [tab, setTab] = useState("umum");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/settings");
      setForm(data);
    })();
  }, []);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        company_name: form.company_name || "EPISI",
        check_in_time: form.check_in_time,
        check_out_time: form.check_out_time,
        schedule_hours: form.schedule_hours || {},
        late_tolerance_minutes: Number(form.late_tolerance_minutes ?? 5),
        early_leave_tolerance_minutes: Number(form.early_leave_tolerance_minutes ?? 5),
      };
      const { data } = await api.put("/settings", payload);
      setForm(data);
      toast.success("Pengaturan tersimpan.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Gagal."); }
    finally { setSaving(false); }
  };

  const setSchedule = (key, field, value) => {
    setForm((p) => ({
      ...p,
      schedule_hours: {
        ...(p.schedule_hours || {}),
        [key]: { ...((p.schedule_hours || {})[key] || {}), [field]: value },
      },
    }));
  };

  if (!form) return <div className="text-gray-500">Memuat...</div>;

  return (
    <div data-testid={TESTIDS.adminSettingsPage} className="space-y-6">
      <div>
        <div className="label-tiny">Pengaturan</div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter mt-2">Pengaturan Sistem</h1>
      </div>

      <div className="flex border-b border-black/10 overflow-x-auto">
        {[
          { k: "umum", l: "Umum" },
          { k: "jadwal", l: "Jadwal Kerja Massal" },
          { k: "wfa", l: "WFA Override" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`settings-tab-${t.k}`}
            className={`px-5 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
              tab === t.k ? "border-klein text-klein" : "border-transparent text-gray-500 hover:text-black"
            }`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "umum" && (
        <div className="max-w-3xl border border-black/10 bg-white p-6 sm:p-8 space-y-6">
          <FF label="Nama Perusahaan">
            <input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="input" />
          </FF>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <FF label="Jam Masuk Default (HH:MM)">
              <input value={form.check_in_time} onChange={(e) => set("check_in_time", e.target.value)} className="input font-mono" placeholder="08:00" />
            </FF>
            <FF label="Jam Pulang Default (HH:MM)">
              <input value={form.check_out_time} onChange={(e) => set("check_out_time", e.target.value)} className="input font-mono" placeholder="17:00" />
            </FF>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <FF label="Toleransi Keterlambatan Check-In (menit)">
              <input data-testid="setting-late-tolerance" type="number" min={0}
                value={form.late_tolerance_minutes ?? 5}
                onChange={(e) => set("late_tolerance_minutes", e.target.value)}
                className="input font-mono" placeholder="5" />
            </FF>
            <FF label="Toleransi Pulang Cepat (menit)">
              <input data-testid="setting-early-tolerance" type="number" min={0}
                value={form.early_leave_tolerance_minutes ?? 5}
                onChange={(e) => set("early_leave_tolerance_minutes", e.target.value)}
                className="input font-mono" placeholder="5" />
            </FF>
          </div>

          <div>
            <div className="label-tiny mb-3">Master Jam Kerja per Jadwal</div>
            <div className="border border-black/10 divide-y divide-black/10">
              <div className="grid grid-cols-3 bg-black text-white text-xs uppercase tracking-widest">
                <div className="px-3 py-2 font-bold">Jadwal</div>
                <div className="px-3 py-2 font-bold">Jam Masuk</div>
                <div className="px-3 py-2 font-bold">Jam Pulang</div>
              </div>
              {Object.entries(JADWAL_KERJA).map(([key, label]) => {
                const sh = (form.schedule_hours || {})[key] || {};
                return (
                  <div key={key} className="grid grid-cols-3 items-center hover:bg-gray-50">
                    <div className="px-3 py-2 text-sm font-semibold">{label}</div>
                    <div className="px-3 py-2">
                      <input data-testid={`sched-${key}-in`}
                        value={sh.check_in || ""} onChange={(e) => setSchedule(key, "check_in", e.target.value)}
                        placeholder="08:00" className="input font-mono py-1.5" />
                    </div>
                    <div className="px-3 py-2">
                      <input data-testid={`sched-${key}-out`}
                        value={sh.check_out || ""} onChange={(e) => setSchedule(key, "check_out", e.target.value)}
                        placeholder="17:00" className="input font-mono py-1.5" />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">Sistem menghitung status <em>Tepat Waktu</em> vs <em>Terlambat</em> berdasarkan jam masuk yang sesuai jadwal kerja masing-masing karyawan.</p>
          </div>

          <button data-testid="settings-save" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-klein text-white px-6 py-3 hover:bg-black text-sm font-semibold disabled:bg-gray-300">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      )}

      {tab === "jadwal" && <BulkJadwalPanel />}
      {tab === "wfa" && <WFAOverridePanel />}

      <style>{`.input { width:100%; border: 1px solid rgba(0,0,0,0.2); padding: 10px 12px; background: white; font-size: 14px; }`}</style>
    </div>
  );
}

/* -------- Bulk Jadwal Kerja Panel -------- */
function BulkJadwalPanel() {
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState({ user_ids: [], start: null, end: null, jadwal_kerja: "WFO", note: "", wfa_permanent: false });
  const [filter, setFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  useEffect(() => {
    (async () => {
      const [u, un] = await Promise.all([api.get("/admin/users/all"), api.get("/work-units")]);
      setUsers(u.data); setUnits(un.data);
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (unitFilter && u.unit_id !== unitFilter) return false;
      if (!q) return true;
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.npp || "").toLowerCase().includes(q)
      );
    });
  }, [users, filter, unitFilter, roleFilter]);

  const toggle = (id) => setForm((f) => ({ ...f, user_ids: f.user_ids.includes(id) ? f.user_ids.filter((x) => x !== id) : [...f.user_ids, id] }));
  const selectFiltered = () => setForm((f) => ({ ...f, user_ids: Array.from(new Set([...f.user_ids, ...filtered.map((u) => u.user_id)])) }));
  const clearSel = () => setForm((f) => ({ ...f, user_ids: [] }));

  const dateSpan = form.start && form.end ? Math.floor((form.end - form.start) / 86400000) + 1 : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (form.user_ids.length === 0 || !form.start || !form.end) return toast.error("Lengkapi user & tanggal.");
    try {
      const { data } = await api.post("/admin/users/bulk-jadwal", {
        user_ids: form.user_ids,
        start_date: format(form.start, "yyyy-MM-dd"),
        end_date: format(form.end, "yyyy-MM-dd"),
        jadwal_kerja: form.jadwal_kerja,
        note: form.note,
        wfa_permanent: !!form.wfa_permanent,
      });
      toast.success(`${data.users_updated} user diperbarui ke ${data.jadwal_kerja}${form.wfa_permanent ? " (WFA Permanen aktif)" : ""}. Override: ${data.override_created} baru, ${data.override_skipped} dilewati.`);
      setForm({ user_ids: [], start: null, end: null, jadwal_kerja: "WFO", note: "", wfa_permanent: false });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal.");
    }
  };

  return (
    <form onSubmit={submit} className="max-w-4xl border border-black/10 bg-white p-6 sm:p-8 space-y-4">
      <p className="text-sm text-gray-600">
        Set Jadwal Kerja (WFO/WFH/WFA/Shift) untuk banyak karyawan dalam rentang tanggal tertentu.
        Untuk WFH/WFA sistem otomatis buat entri override per-hari sehingga karyawan bebas dari radius kantor.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FF label="Tanggal Mulai"><DateBtn value={form.start} onChange={(d) => setForm({ ...form, start: d })} /></FF>
        <FF label="Tanggal Selesai"><DateBtn value={form.end} onChange={(d) => setForm({ ...form, end: d })} /></FF>
        <FF label="Jadwal Kerja">
          <select value={form.jadwal_kerja} onChange={(e) => setForm({ ...form, jadwal_kerja: e.target.value })} className="input">
            {Object.entries(JADWAL_KERJA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </FF>
      </div>
      <FF label="Catatan (opsional)">
        <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="input" placeholder="Misal: pergantian shift Q2" />
      </FF>

      <FF label="WFA Permanen">
        <label className="flex items-center gap-3 border border-black/20 px-3 py-2.5 bg-white cursor-pointer">
          <input data-testid="bulk-wfa-permanent" type="checkbox" className="w-4 h-4"
            checked={!!form.wfa_permanent}
            onChange={(e) => setForm({ ...form, wfa_permanent: e.target.checked })} />
          <span className="text-sm">
            {form.wfa_permanent
              ? "Ya — karyawan terpilih akan diberi WFA Permanen (Check-In tanpa cek radius; selfie tetap wajib)."
              : "Tidak — WFA Permanen tidak diubah."}
          </span>
        </label>
      </FF>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="label-tiny">Pilih Karyawan ({form.user_ids.length} dipilih)</div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={selectFiltered} className="text-xs border border-black/20 px-2 py-1 hover:bg-black hover:text-white">+ Semua hasil filter</button>
            <button type="button" onClick={clearSel} className="text-xs border border-black/20 px-2 py-1 hover:bg-black hover:text-white">Kosongkan</button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Cari nama / NPP / email..." className="input sm:col-span-2" />
          <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)} className="input">
            <option value="">Semua Unit</option>
            {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.name}</option>)}
          </select>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="input">
            <option value="">Semua Role</option>
            {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="border border-black/20 max-h-72 overflow-y-auto divide-y divide-black/5">
          {filtered.length === 0 && <div className="p-4 text-center text-sm text-gray-500">Tidak ada karyawan cocok.</div>}
          {filtered.map((u) => {
            const unit = units.find((x) => x.unit_id === u.unit_id);
            return (
              <label key={u.user_id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={form.user_ids.includes(u.user_id)} onChange={() => toggle(u.user_id)} />
                <div className="flex-1 flex items-center gap-3">
                  <span className="font-mono text-xs w-20 shrink-0 text-gray-500">{u.npp || "—"}</span>
                  <span className="font-semibold text-sm flex-1 truncate">{u.name}</span>
                  <span className="text-xs text-gray-500 hidden md:inline w-32 truncate">{unit ? unit.name : "—"}</span>
                  <span className="text-[10px] uppercase tracking-widest border border-black/20 px-1.5 py-0.5">{ROLES[u.role] || u.role}</span>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <button type="submit" disabled={form.user_ids.length === 0 || !form.start || !form.end}
        className="inline-flex items-center gap-2 bg-klein text-white px-6 py-3 hover:bg-black text-sm font-semibold disabled:bg-gray-300">
        <Users2 className="w-4 h-4" /> Terapkan ({form.user_ids.length} user × {dateSpan} hari)
      </button>
    </form>
  );
}

/* -------- WFA Override Panel (pindah dari halaman terpisah) -------- */
function WFAOverridePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/wfa-overrides");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doDelete = async (w) => {
    if (!window.confirm(`Hapus override untuk ${w.user_name} pada ${w.date}?`)) return;
    try {
      await api.delete(`/admin/wfa-overrides/${w.wfa_id}`);
      toast.success("Dihapus.");
      load();
    } catch (_e) { toast.error("Gagal hapus."); }
  };

  return (
    <div className="border border-black/10 bg-white">
      <div className="p-4 border-b border-black/10 text-sm text-gray-600">
        Daftar override WFA per-hari. Otomatis dibuat oleh "Jadwal Kerja Massal" (WFH/WFA). Bisa dihapus jika diperlukan.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-black text-white text-left">
            <tr><Th>Tanggal</Th><Th>Karyawan</Th><Th>Catatan</Th><Th>Aksi</Th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="p-8 text-center text-gray-500">Memuat...</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-gray-500">Belum ada override.</td></tr>}
            {items.map((w) => (
              <tr key={w.wfa_id} className="border-t border-black/10 hover:bg-gray-50">
                <Td className="font-mono">{w.date}</Td>
                <Td className="font-semibold">{w.user_name}</Td>
                <Td className="text-gray-600">{w.note || "—"}</Td>
                <Td>
                  <button onClick={() => doDelete(w)} className="border border-black/20 hover:bg-signal-late hover:text-white p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FF({ label, children, required }) {
  return <div><div className="label-tiny mb-2">{label}{required && <span className="text-signal-late"> *</span>}</div>{children}</div>;
}
function DateBtn({ value, onChange }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start rounded-none border-black/20 bg-white hover:bg-gray-50">
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "dd MMM yyyy") : "Pilih tanggal"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-none" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} initialFocus />
      </PopoverContent>
    </Popover>
  );
}
const Th = ({ children }) => <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
